import { ApolloError } from '@apollo/client';
import {
  ChartColorContext,
  ChartErrorComponent,
  defaultEChartsOptionForInterval,
  DeviceWithInstalledPower,
  ECHARTS_CONTAINER_HEIGHT as height,
  nowMarkLineSeriesOption,
} from '@e2m-customer-portal/domains';
import { useLocale } from '@e2m-customer-portal/hooks';
import { convertMeasure, InputFieldType, isDefined } from '@e2m-customer-portal/shared';
import { eachMinuteOfInterval } from 'date-fns';
import { EChartsOption, EChartsType, SeriesOption } from 'echarts';
import { DeviceTimeSeries, Forecast, Interval, MarketLocationTimeSeries } from 'generated/graphql';
import { ReactElement, useContext, useRef } from 'react';
import EChartsReact, { EChartsReactRef } from 'react-echarts-library';
import { useTranslation } from 'react-i18next';
import { ChartColor, getOutages, getOutagesData } from './utils';
import { Button } from '@mui/material';
import { ZoomOutIcon } from 'assets/icons';
import { MarkAreaOption } from 'echarts/types/dist/shared';

interface PVProductionChartProps {
  dataInterval: Interval;
  deviceTimeSeries?: readonly DeviceTimeSeries[];
  device?: DeviceWithInstalledPower;
  deviceTimeSeriesError?: ApolloError;
  calculatedPVProductionTimeSeries?: MarketLocationTimeSeries;
  calculatedPVProductionTimeSeriesError?: ApolloError;
  forecasts?: readonly Forecast[];
  forecastError?: ApolloError;
  onChartReadyCallback: (instance: EChartsType) => void;
  resetZoom: (ref: EChartsReactRef) => void;
  isZoomed?: boolean;
}

export function PVProductionChart({
  dataInterval,
  deviceTimeSeries,
  device,
  deviceTimeSeriesError,
  calculatedPVProductionTimeSeries,
  calculatedPVProductionTimeSeriesError,
  forecasts,
  forecastError,
  onChartReadyCallback,
  resetZoom,
  isZoomed,
}: Readonly<PVProductionChartProps>): ReactElement<PVProductionChartProps> {
  const [t] = useTranslation();
  const { code: locale } = useLocale();
  const chartColorVariant = useContext(ChartColorContext);
  const chartRef = useRef<EChartsReactRef>(null);

  const sortedDatapoints = calculatedPVProductionTimeSeries
    ? [...calculatedPVProductionTimeSeries.datapoints].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
    : undefined;

  const calculatedPVProductionSeriesData = sortedDatapoints?.map((datapoint) => [
    datapoint.timestamp,
    convertMeasure({ value: datapoint.value, unit: 'MW' }, 'kW').value,
  ]);

  const productionSum = forecasts?.reduce(
    (chartData: [number, number | undefined | null][], forecast) => {
      for (const slice of forecast.slices) {
        const production = slice.fields.find((field) => field.type === InputFieldType.PRODUCTION);
        const productionValue = production ? convertMeasure(production.value, 'kW').value : null;
        const chartDataIndex = chartData.findIndex(([timestamp]) => timestamp === slice.date.getTime());
        if (chartDataIndex !== -1 && production)
          chartData[chartDataIndex] = [
            chartData[chartDataIndex][0],
            chartData[chartDataIndex][1] == null
              ? productionValue
              : chartData[chartDataIndex][1]! + (productionValue ?? 0),
          ];
      }
      return chartData;
    },
    eachMinuteOfInterval(dataInterval, { step: 15 }).map((date) => [date.getTime(), null]) as [
      number,
      number | undefined | null,
    ][],
  );

  const deviceForecast = [...(forecasts?.find((forecast) => forecast.deviceId === device?.id)?.slices ?? [])].sort(
    (a, b) => a.date.getTime() - b.date.getTime(),
  );

  const outagesData = getOutagesData(deviceForecast);
  const outagesMarkedAreas = getOutages(deviceForecast);

  const actualPowerSum = deviceTimeSeries?.reduce(
    (chartData: [number, number | undefined | null][], timeSeries) => {
      for (const datapoint of timeSeries.datapoints) {
        const actualPower =
          datapoint.actualPower != null
            ? convertMeasure({ value: datapoint.actualPower, unit: 'MW' }, 'kW').value
            : null;
        const chartDataIndex = chartData.findIndex(([timestamp]) => timestamp === datapoint.date.getTime());
        if (chartDataIndex !== -1 && actualPower != null)
          chartData[chartDataIndex] = [
            chartData[chartDataIndex][0],
            chartData[chartDataIndex][1] == null ? actualPower : chartData[chartDataIndex][1]! + (actualPower ?? 0),
          ];
      }
      return chartData;
    },
    eachMinuteOfInterval(dataInterval, { step: 15 }).map((date) => [date.getTime(), null]) as [
      number,
      number | undefined | null,
    ][],
  );

  const seriesOption: SeriesOption = {
    type: 'line',
    step: 'end',
    symbol: 'circle',
    showSymbol: false,
    tooltip: {
      valueFormatter: (value) => (value == null ? '-' : `${Math.round(Number(value)).toLocaleString(locale)} kW`),
    },
  };

  const defaultEChartsOption = defaultEChartsOptionForInterval(t, dataInterval);
  const option: EChartsOption = {
    ...defaultEChartsOption,
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: '{value} kW',
        color: '#888888',
      },
      splitLine: {
        lineStyle: {
          color: '#F0F0F0',
        },
      },
    },
    legend: {
      ...defaultEChartsOption.legend,
      selected: {
        [t('COLOCATION_OVERVIEW_CHARTS_CALCULATED_PV_PRODUCTION')]: false,
      },
    },
    series: [
      {
        ...seriesOption,
        name: t('COLOCATION_OVERVIEW_CHARTS_PV_PRODUCTION_TARGET'),
        data: productionSum,
        color: ChartColor.PrimaryFull[chartColorVariant],
      },
      {
        ...seriesOption,
        name: t('COLOCATION_OVERVIEW_CHARTS_PV_PRODUCTION_ACTUAL_POWER'),
        data: actualPowerSum,
        color: ChartColor.SecondaryFull[chartColorVariant],
      },
      calculatedPVProductionSeriesData
        ? {
            ...seriesOption,
            name: calculatedPVProductionSeriesData
              ? t('COLOCATION_OVERVIEW_CHARTS_CALCULATED_PV_PRODUCTION')
              : undefined,
            data: calculatedPVProductionSeriesData,
            color: ChartColor.TertiaryFull[chartColorVariant],
          }
        : undefined,
      nowMarkLineSeriesOption,
      outagesData.some((item) => item !== null)
        ? ({
            type: 'line',
            lineStyle: {
              width: 0,
            },
            name: t('DOWNTIME_SET'),
            connectNulls: false,
            symbol: 'square',
            showSymbol: false,
            tooltip: {
              valueFormatter: () => '',
            },
            color: '#FF0A1180',
            data: outagesData,
            markArea: {
              label: { show: false },
              z: 9,
              itemStyle: {
                color: '#FF0A11',
                opacity: 0.5,
              },
              data: outagesMarkedAreas
                .map((s) => {
                  if (s.isOutage) return [{ xAxis: +s.interval.start }, { xAxis: +s.interval.end }];
                })
                .filter(isDefined),
            } as MarkAreaOption,
          } satisfies SeriesOption)
        : (undefined as unknown as SeriesOption),
    ].filter(isDefined),
  };

  return (
    <div className="relative">
      {isZoomed && (
        <Button
          className="absolute top-8 left-32 w-fit px-1 bg-white/45 z-40"
          style={{ color: ChartColor.PrimaryFull[chartColorVariant] }}
          color="inherit"
          variant="outlined"
          onClick={() => chartRef.current && resetZoom(chartRef.current)}
        >
          <ZoomOutIcon style={{ fill: ChartColor.PrimaryFull[chartColorVariant] }} />
        </Button>
      )}
      <EChartsReact
        style={{ height }}
        option={option}
        opts={{ locale, renderer: 'svg' }}
        data-testid="PV-production-chart"
        onChartReady={onChartReadyCallback}
        ref={chartRef}
      />
      {(deviceTimeSeriesError || calculatedPVProductionTimeSeriesError || forecastError) && (
        <ChartErrorComponent errors={[deviceTimeSeriesError, calculatedPVProductionTimeSeriesError, forecastError]} />
      )}
    </div>
  );
}
